import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useRestored } from "@/lib/restore";
import { RefreshLibraryButton } from "@/components/RefreshLibraryButton";
import { Search, Loader2, Play, Heart, HeartOff, Check, CloudDownload, Shuffle, Users, Sparkles } from "lucide-react";
import { SelectMenu } from "@/components/Selection";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrackTable } from "@/components/TrackTable";
import { AlbumGrid } from "@/components/AlbumCard";
import { Cover } from "@/components/Cover";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { LibrarySwitch, useOwnerView } from "@/components/LibrarySwitch";
import { usePlayer } from "@/context/PlayerContext";
import { api } from "@/lib/api";
import { supported } from "@/lib/offline";
import { useAutoPins, setAutoFavorites } from "@/lib/autoPins";
import { MAX_SEEDS, useBuildMix } from "@/lib/mix";
import { useRatings } from "@/lib/ratings";
import { RatedList } from "@/components/RatedList";
import { ThumbsDown, ThumbsUp } from "lucide-react";

const SORTS = { recent: "Recently Added", title: "Title", artist: "Artist", album: "Album", year: "Year", plays: "Most Played" };

export function Songs() {
  const { viewing } = useOwnerView();
  const [params, setParams] = useSearchParams();
  const urlQ = params.get("q") || "";
  const sort = params.get("sort") || "recent";
  const [q, setQ] = useState(urlQ);
  const [deb, setDeb] = useState(urlQ);
  const [restored, remember] = useRestored();
  const skipLoad = useRef(!!restored);
  const [tracks, setTracks] = useState(restored?.tracks || []);
  const [total, setTotal] = useState(restored?.total || 0);
  const [loading, setLoading] = useState(!restored);
  const p = usePlayer();
  useEffect(() => { const t = setTimeout(() => setDeb(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { if (urlQ !== deb) { setQ(urlQ); setDeb(urlQ); } }, [urlQ]); // eslint-disable-line react-hooks/exhaustive-deps
  const setUrl = (patch) => {
    const next = { sort, ...(urlQ ? { q: urlQ } : {}), ...patch };
    if (!next.q) delete next.q;
    if (next.sort === "recent") delete next.sort;
    setParams(next, { replace: true });
  };
  useEffect(() => { if (deb !== urlQ) setUrl({ q: deb }); }, [deb]); // eslint-disable-line react-hooks/exhaustive-deps
  const reqId = useRef(0);
  const load = useCallback(async (offset = 0) => {
    const my = ++reqId.current;
    setLoading(true);
    const { data } = await api.get("/tracks", { params: { q: deb, sort, limit: 100, offset } });
    if (my !== reqId.current) return;
    setTracks((prev) => (offset ? [...prev, ...data.items] : data.items));
    setTotal(data.total);
    setLoading(false);
  }, [deb, sort]);
  useEffect(() => {
    if (skipLoad.current) { skipLoad.current = false; return; }
    load(0);
  }, [load]);
  useEffect(() => { if (!loading) remember({ tracks, total }); }, [tracks, total, loading]); // eslint-disable-line react-hooks/exhaustive-deps
  const [shuffling, setShuffling] = useState(false);
  const selRef = useRef(null);
  const [selInfo, setSelInfo] = useState([0, 0]);
  const songSel = { mode: selInfo[0] > 0, setMode: null, count: selInfo[0], total: selInfo[1],
    all: () => selRef.current?.all(), none: () => selRef.current?.none(), invert: () => selRef.current?.invert(), exit: () => selRef.current?.none() };
  const shuffleAll = async () => {
    setShuffling(true);
    try {
      const { data } = await api.get("/tracks", { params: { q: deb, sort: "random", limit: 300 } });
      p.playTracks(data.items);
      toast.success(`Shuffling ${data.items.length} song${data.items.length === 1 ? "" : "s"}`);
    } catch { toast.error("Couldn't start shuffle"); }
    setShuffling(false);
  };

  return (
    <div>
      <LibrarySwitch />
      <PageHeader stack eyebrow={`${total} song${total === 1 ? "" : "s"}`} title="Songs" testid="songs-title">
        <RefreshLibraryButton onDone={() => load()} />
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search songs" data-testid="song-search-input" className="h-10 w-60 rounded-full bg-white pl-9" />
        </div>
        <Select value={sort} onValueChange={(v) => setUrl({ sort: v })}>
          <SelectTrigger className="h-10 w-40 rounded-full bg-white" data-testid="song-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={() => p.playTracks(tracks)} disabled={!tracks.length} data-testid="songs-play-all"
          className="flex h-10 items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm text-white transition-transform hover:scale-[1.03] disabled:opacity-40">
          <Play size={14} fill="currentColor" /> Play all
        </button>
        <button onClick={shuffleAll} disabled={!total || shuffling} data-testid="songs-shuffle-all"
          className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 bg-white px-4 text-sm hover:border-zinc-900 disabled:opacity-40">
          {shuffling ? <Loader2 size={14} className="animate-spin" /> : <Shuffle size={14} />} Shuffle all
        </button>
        {!viewing && <SelectMenu s={songSel} noun="song" testid="songs-select" picks={[
          { label: "Select favorites", icon: Heart, testid: "favorites", count: tracks.filter((t) => t.starred).length, onClick: () => selRef.current?.where((t) => t.starred) },
          { label: "Select non-favorites", icon: HeartOff, testid: "non-favorites", count: tracks.filter((t) => !t.starred).length, onClick: () => selRef.current?.where((t) => !t.starred) },
        ]} />}
      </PageHeader>
      <TrackTable tracks={tracks} showCover selectable={!viewing} readOnly={viewing} sortable={{ sort, onSort: (f) => setUrl({ sort: f }) }} selectionRef={selRef} onSelectionChange={(n, tot) => setSelInfo([n, tot])} onTracksChange={(next) => { setTotal((t) => t - (tracks.length - next.length)); setTracks(next); }} />
      {loading && <Loader2 className="mt-6 animate-spin text-zinc-400" />}
      {!loading && tracks.length < total && (
        <button onClick={() => load(tracks.length)} data-testid="songs-load-more" className="mt-10 rounded-full border border-zinc-300 bg-white px-6 py-2 text-sm hover:bg-zinc-900 hover:text-white">Load more</button>
      )}
    </div>
  );
}

const KeepFavoritesOffline = () => {
  const { auto, status } = useAutoPins();
  if (!supported()) return null;
  return (
    <button onClick={() => setAutoFavorites(!auto.favorites)} data-testid="favorites-keep-offline" data-on={auto.favorites}
      className={`flex h-10 items-center gap-2 rounded-full border px-4 text-sm transition-colors ${auto.favorites ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-900"}`}>
      {status.syncing && auto.favorites ? <Loader2 size={14} className="animate-spin" /> : auto.favorites ? <Check size={14} /> : <CloudDownload size={14} />}
      {auto.favorites ? "Kept offline" : "Keep offline"}
    </button>
  );
};

export function Favorites() {
  const [tracks, setTracks] = useState(null);
  const [albums, setAlbums] = useState(null);
  const [filter, setFilter] = useState("");
  const p = usePlayer();
  const buildMix = useBuildMix();
  const ratings = useRatings();
  useEffect(() => {
    api.get("/tracks", { params: { starred: true, limit: 500 } }).then((r) => setTracks(r.data.items));
    api.get("/albums", { params: { starred: true, limit: 200 } }).then((r) => setAlbums(r.data.items));
  }, []);
  if (!tracks || !albums) return <Loader2 className="animate-spin text-zinc-400" />;
  const f = filter.trim().toLowerCase();
  const shown = f ? tracks.filter((t) => [t.title, t.artist, t.album, t.genre].some((x) => (x || "").toLowerCase().includes(f))) : tracks;
  const seedCount = Math.min(shown.length, MAX_SEEDS);
  const artists = Object.values(tracks.reduce((m, t) => {
    const k = (t.artist || "").toLowerCase();
    m[k] = m[k] || { name: t.artist, count: 0, album_id: t.album_id, lib: t.lib };
    m[k].count += 1;
    return m;
  }, {})).sort((a, b) => b.count - a.count);
  return (
    <div>
      <PageHeader stack eyebrow="Starred" title="Favorites" testid="favorites-title">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter favorites" data-testid="favorites-filter-input" className="h-10 w-48 rounded-full bg-white pl-9" />
        </div>
        <KeepFavoritesOffline />
        <button onClick={() => buildMix(f ? shown : [...shown].sort(() => Math.random() - 0.5))} disabled={!shown.length} data-testid="favorites-build-mix"
          title={f ? `Build a mix around the ${seedCount} filtered favorite${seedCount === 1 ? "" : "s"}` : `Build a mix around ${seedCount} of your favorites`}
          className="flex h-10 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-4 text-sm text-amber-900 hover:border-amber-600 disabled:opacity-40">
          <Sparkles size={14} /> Build a mix{f && shown.length ? <span className="font-mono text-[10px] text-amber-700/70">{seedCount}</span> : null}
        </button>
        <button onClick={() => p.playTracks([...shown].sort(() => Math.random() - 0.5))} disabled={!shown.length} data-testid="favorites-shuffle"
          className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 bg-white px-4 text-sm hover:border-zinc-900 disabled:opacity-40">
          <Shuffle size={14} /> Shuffle
        </button>
        <button onClick={() => p.playTracks(shown)} disabled={!shown.length} data-testid="favorites-play-all"
          className="flex h-10 items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm text-white disabled:opacity-40">
          <Play size={14} fill="currentColor" /> Play favorites
        </button>
      </PageHeader>
      <Tabs defaultValue="songs">
        <TabsList className="mb-8 bg-zinc-100">
          <TabsTrigger value="songs" data-testid="favorites-tab-songs">Songs · {f ? `${shown.length} of ${tracks.length}` : tracks.length}</TabsTrigger>
          <TabsTrigger value="albums" data-testid="favorites-tab-albums">Albums · {albums.length}</TabsTrigger>
          <TabsTrigger value="artists" data-testid="favorites-tab-artists">Artists · {artists.length}</TabsTrigger>
          <TabsTrigger value="liked" data-testid="favorites-tab-liked"><ThumbsUp size={12} className="mr-1.5" />Liked · {ratings.liked.size}</TabsTrigger>
          <TabsTrigger value="disliked" data-testid="favorites-tab-disliked" className="text-zinc-400 data-[state=active]:text-zinc-900"><ThumbsDown size={12} className="mr-1.5" />Disliked · {ratings.disliked.size}</TabsTrigger>
        </TabsList>
        <TabsContent value="songs">
          {shown.length ? <TrackTable tracks={shown} showCover selectable removable={false} />
            : f ? <EmptyState icon={Search} title="No favorites match" text={`Nothing in your favorites matches “${filter.trim()}”.`} />
            : <EmptyState icon={Heart} title="No favorite songs" text="Tap the heart on any song to keep it here." />}
        </TabsContent>
        <TabsContent value="albums">
          {albums.length ? <AlbumGrid albums={albums} /> : <EmptyState icon={Heart} title="No favorite albums" text="Star an album from its page." />}
        </TabsContent>
        <TabsContent value="artists">
          {artists.length ? (
            <ul className="divide-y divide-zinc-100" data-testid="favorites-artists">
              {artists.map((a) => (
                <li key={a.name} className="flex items-center gap-4 py-3">
                  <Cover albumId={a.album_id} className="h-11 w-11 shrink-0" rounded="rounded-full" iconSize={14} />
                  <Link to={`/artist?name=${encodeURIComponent(a.name)}`} className="flex-1 truncate text-sm font-medium hover:underline" data-testid={`favorites-artist-${a.name}`}>{a.name}</Link>
                  <span className="font-mono text-xs text-zinc-500">{a.count} favorite{a.count === 1 ? "" : "s"}</span>
                  <button onClick={() => p.playTracks(tracks.filter((t) => (t.artist || "").toLowerCase() === a.name.toLowerCase()))} data-testid={`favorites-artist-play-${a.name}`}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 hover:border-zinc-900"><Play size={12} fill="currentColor" /></button>
                </li>
              ))}
            </ul>
          ) : <EmptyState icon={Users} title="No favorite artists yet" text="Artists appear here once you favorite their songs." />}
        </TabsContent>
        <TabsContent value="liked"><RatedList rating={1} ids={ratings.liked} /></TabsContent>
        <TabsContent value="disliked"><RatedList rating={-1} ids={ratings.disliked} /></TabsContent>
      </Tabs>
    </div>
  );
}
